import React, { useState, useEffect } from 'react';
import AppLoadingState, { LoadingSpinnerRing } from './common/AppLoadingState';
import { toast } from 'react-hot-toast';
import api from '../utils/api';
import { AppPageShell, PageHeader } from './common/ui';

const TwilioManagement = () => {
  const [activeTab, setActiveTab] = useState('numbers');
  const [encryptedKey, setEncryptedKey] = useState('');
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [showGenerateKey, setShowGenerateKey] = useState(false);
  const [showGeneratedKey, setShowGeneratedKey] = useState(false);
  const [accountForm, setAccountForm] = useState({
    account_sid: '',
    auth_token: ''
  });

  // Step 1: Generate/Select Encrypted Key
  const handleGenerateKey = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.createEncryptedKey(
        accountForm.account_sid,
        accountForm.auth_token
      );
      if (response.encrypted_key) {
        setEncryptedKey(response.encrypted_key);
        setShowGeneratedKey(true);
        toast.success('Encrypted key generated successfully');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to generate encrypted key');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Manage Numbers
  const handleImportNumbers = async () => {
    if (selectedNumbers.length === 0) {
      toast.error('Please select at least one number to import');
      return;
    }

    setLoading(true);
    try {
      await api.uploadInboundNumbers(encryptedKey, selectedNumbers);
      toast.success('Numbers imported successfully');
      setSelectedNumbers([]);
      fetchNumbers();
    } catch (error) {
      toast.error(error.message || 'Failed to import numbers');
    } finally {
      setLoading(false);
    }
  };

  const fetchNumbers = async () => {
    if (!encryptedKey) return;
    
    setLoading(true);
    try {
      const response = await api.getNumbers(encryptedKey);
      setNumbers(response.inbound_numbers || []);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch numbers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (encryptedKey && !showGeneratedKey) {
      fetchNumbers();
    }
  }, [encryptedKey, showGeneratedKey]);

  const handleNumberSelect = (number) => {
    setSelectedNumbers(prev => {
      if (prev.includes(number)) {
        return prev.filter(n => n !== number);
      }
      return [...prev, number];
    });
  };

  const filteredNumbers = numbers.filter(number => 
    number.phone_number.includes(searchQuery)
  );

  if (showGeneratedKey) {
    return (
      <AppPageShell maxWidthClass="max-w-3xl">
        <PageHeader
          eyebrow="Telephony"
          title="Encrypted key ready"
          description="Store this key securely. You will need it to import and manage Twilio numbers below."
        />
        <div className="space-y-6">
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">
              Encrypted Key
            </label>
            <input
              type="text"
              value={encryptedKey}
              readOnly
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-lg bg-white"
            />
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <p className="text-lg text-yellow-800">
              Save this! The Encrypted Key will only be displayed to you for security and cannot be
              retrieved later without regenerating.
            </p>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => {
                setShowGeneratedKey(false);
                setActiveTab('numbers');
              }}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
            >
              Continue to Number Management
            </button>
          </div>
        </div>
      </AppPageShell>
    );
  }

  return (
    <AppPageShell maxWidthClass="max-w-6xl">
      <PageHeader
        eyebrow="Telephony"
        title="Twilio number management"
        description="Connect an encrypted key, import numbers from Twilio, and keep them in sync with this workspace."
      />
      <div className="mb-8">
        <div className="flex space-x-4 border-b border-[var(--app-border)]">
          <button
            className={`py-2 px-4 ${activeTab === 'numbers' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('numbers')}
          >
            Numbers
          </button>
          <button
            className={`py-2 px-4 ${activeTab === 'generate-key' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('generate-key')}
          >
            Generate Key
          </button>
        </div>
      </div>

      {activeTab === 'numbers' && (
        <div className="space-y-6">
          {/* Step 1: Select or Generate Encrypted Key */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Step 1: Select or Generate Encrypted Key</h2>
            <div className="flex items-center space-x-4">
              <input
                type="text"
                value={encryptedKey}
                onChange={(e) => setEncryptedKey(e.target.value)}
                placeholder="Enter encrypted key"
                className="flex-1 border rounded-lg px-4 py-2"
              />
              <button
                onClick={() => {
                  setActiveTab('generate-key');
                  setShowGenerateKey(true);
                }}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
              >
                Generate New Key
              </button>
            </div>
          </div>

          {/* Step 2: Manage Twilio Numbers */}
          {encryptedKey && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Step 2: Manage Twilio Numbers</h2>
              <div className="mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search numbers"
                  className="w-full border rounded-lg px-4 py-2"
                />
              </div>

              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span>{selectedNumbers.length} numbers selected</span>
                  <button
                    onClick={handleImportNumbers}
                    disabled={selectedNumbers.length === 0}
                    className={`px-4 py-2 rounded-lg ${
                      selectedNumbers.length > 0
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Import Selected Numbers
                  </button>
                </div>
                <div className="border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            checked={selectedNumbers.length === filteredNumbers.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedNumbers(filteredNumbers.map(n => n.phone_number));
                              } else {
                                setSelectedNumbers([]);
                              }
                            }}
                            className="h-4 w-4 text-blue-600 rounded"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Twilio Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Import Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loading ? (
                        <tr>
                          <td colSpan="4" className="px-6 py-10">
                            <AppLoadingState layout="compact" showLabel={false} className="!min-h-0 py-0" />
                          </td>
                        </tr>
                      ) : filteredNumbers.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-6 py-4 text-center">
                            No numbers found
                          </td>
                        </tr>
                      ) : (
                        filteredNumbers.map((number) => (
                          <tr key={number.phone_number}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedNumbers.includes(number.phone_number)}
                                onChange={() => handleNumberSelect(number.phone_number)}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {number.phone_number}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                number.imported
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {number.imported ? 'Imported' : 'Not Imported'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => {/* Handle delete */}}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'generate-key' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Generate Encrypted Key</h2>
          <form onSubmit={handleGenerateKey} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account SID
              </label>
              <input
                type="text"
                value={accountForm.account_sid}
                onChange={(e) => setAccountForm({ ...accountForm, account_sid: e.target.value })}
                placeholder="AC..."
                className="w-full border rounded-lg px-4 py-2"
              />
              <p className="mt-1 text-sm text-gray-500">
                Hint: Account SID typically starts with "AC" and is 34 characters long.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Auth Token
              </label>
              <input
                type="password"
                value={accountForm.auth_token}
                onChange={(e) => setAccountForm({ ...accountForm, auth_token: e.target.value })}
                className="w-full border rounded-lg px-4 py-2"
              />
              <p className="mt-1 text-sm text-gray-500">
                Hint: Auth Token is typically 32 characters long.
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700">
                The Encrypted Key will only be displayed to you for security and cannot be retrieved later without regenerating.
              </p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-300"
            >
              {loading ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <LoadingSpinnerRing size="sm" tone="onDark" />
                  Generating…
                </span>
              ) : (
                'Generate Key'
              )}
            </button>
          </form>
        </div>
      )}
    </AppPageShell>
  );
};

export default TwilioManagement; 